/* Cellar quote engine.
 *
 * Replaces a calculator whose hardcoded unit list had 6 of 10 rated capacities
 * wrong, labelled two split systems as self-contained, and quoted a decibel
 * figure for a series the manufacturer does not rate for noise. Nothing is
 * hardcoded here: units, prices, variants and rated capacity all come from
 * Shopify at render time.
 *
 * Rated capacity is the wcc.capacity_cu_ft metafield, set from each product's
 * own spec table and, for the two Quantum units, from WhisperKOOL's owner's
 * manual where the page was wrong or silent.
 *
 * Customer-facing strings live here as well as in the UI. American spelling
 * throughout, to match the catalog.
 */
(function (root, factory) {
  if (typeof module !== 'undefined' && module.exports) module.exports = factory();
  else root.WCCQuote = factory();
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  // Margins we add to the room volume before matching it against a published
  // capacity. Ours, not the manufacturer's, and the UI says so. Manufacturers
  // rate capacity for a sealed, insulated room; WhisperKOOL's manual notes that
  // glass, stone and concrete all push the real load higher.
  var ALLOWANCE = {
    glass:        { factor: 1.35, label: 'glass wall or door, +35%' },
    noVapour:     { factor: 1.15, label: 'no vapor barrier, +15%' },
    hotAmbient:   { factor: 1.15, label: 'exhaust room over 85°F, +15%' },
    standardDoor: { factor: 1.10, label: 'uninsulated door, +10%' }
  };

  var MAX_QTY = 3;
  var MAX_PICKS = 4;

  /* Every unit that covers the sizing volume is adequate by the manufacturer's
   * own rating, so ranking by closest fit is the wrong instinct: it recommended
   * a $6,119 split system needing an HVAC-R technician for a 384 cu ft closet
   * that a $3,055 plug-in unit cools. Rank by price, but refuse to recommend
   * grossly oversized equipment, which short-cycles and holds humidity badly. */
  var OVERSIZE_LIMIT = 3;

  function sizing(input) {
    var volume = input.length * input.width * input.height;
    var v = volume, notes = [];
    if (input.glass) { v *= ALLOWANCE.glass.factor; notes.push(ALLOWANCE.glass.label); }
    if (!input.vapourBarrier) { v *= ALLOWANCE.noVapour.factor; notes.push(ALLOWANCE.noVapour.label); }
    if (input.ambient > 85) { v *= ALLOWANCE.hotAmbient.factor; notes.push(ALLOWANCE.hotAmbient.label); }
    if (input.standardDoor) { v *= ALLOWANCE.standardDoor.factor; notes.push(ALLOWANCE.standardDoor.label); }
    return { volume: Math.round(volume), sizingVolume: Math.round(v), notes: notes };
  }

  /* Heat-load estimate. Shown for orientation and labelled as such. It does NOT
   * decide which products are recommended: that runs off published capacity.
   *
   * Conduction flows through the room's ENVELOPE AREA, not its volume, so we
   * size against the real surface area (4 walls + ceiling + floor). Calibrated
   * so a baseline build (R-19, 75F exhaust ambient, vapor barrier, insulated
   * door, no glass) lands on our own published shortcut chart:
   *   100->1500, 200->2500, 400->3500, 650->4500, 1000->5500, 1500->6500,
   *   2000->7000 BTU/hr. That chart is a unit-capacity-to-target figure, so the
   *   constant below bundles infiltration, product load and headroom on top of
   *   bare wall conduction (which for a 1,080 cu ft cellar is only ~700 BTU/hr).
   * The previous version multiplied volume by a fixed number as a stand-in for
   * area and overstated the load ~4.5x. */
  function btuEstimate(input) {
    var wallArea = 2 * (input.length + input.width) * input.height;
    var capArea = 2 * (input.length * input.width);
    var area = wallArea + capArea;
    var deltaT = Math.max(input.ambient - 55, 10);
    // Diminishing returns for large rooms, matching the chart's flattening
    // above ~1,500 cu ft while keeping the small-room surface penalty.
    var effArea = area / (1 + area / 1400);
    // C * U * A_eff * dT. rFactor is the wall U-value (1/R), so insulation
    // scales the estimate proportionally.
    var btu = 9.9 * input.rFactor * effArea * deltaT;
    if (input.glass) btu *= 1.35;
    if (!input.vapourBarrier) btu *= 1.15;
    if (input.standardDoor) btu *= 1.10;
    btu *= 1.18; // safety margin (the chart figures already include this)
    if (btu < 1000) btu = 1000;
    if (btu > 20000) btu = 20000;
    return Math.round(btu / 50) * 50;
  }

  function pickUnits(units, sizingVolume) {
    var scored = [];
    for (var i = 0; i < units.length; i++) {
      var u = units[i];
      if (!u.cuft || !u.variants || !u.variants.length) continue;
      var qty = Math.ceil(sizingVolume / u.cuft);
      if (qty < 1) qty = 1;
      if (qty > MAX_QTY) continue;
      var v = u.variants.filter(function (x) { return x.available; })[0] || u.variants[0];
      scored.push({
        unit: u, qty: qty, variant: v,
        covered: u.cuft * qty, total: v.price * qty,
        headroom: (u.cuft * qty) - sizingVolume
      });
    }
    var sane = scored.filter(function (s) { return s.covered <= sizingVolume * OVERSIZE_LIMIT; });
    if (!sane.length) sane = scored;
    sane.sort(function (a, b) {
      return a.qty - b.qty || a.total - b.total || a.headroom - b.headroom;
    });
    var single = sane.filter(function (s) { return s.qty === 1; });
    var multi = sane.filter(function (s) { return s.qty > 1; });
    if (!single.length) return multi.slice(0, MAX_PICKS);

    var out = single.slice(0, MAX_PICKS);
    /* One unit is the simpler install and stays the default, but a multi-unit
     * build can be thousands cheaper AND skip the HVAC-R technician. A
     * contractor should see that rather than have it hidden. */
    var cheapestMulti = multi.slice().sort(function (a, b) { return a.total - b.total; })[0];
    if (cheapestMulti && cheapestMulti.total < single[0].total) {
      out = single.slice(0, MAX_PICKS - 1);
      out.push(cheapestMulti);
    }
    return out;
  }

  /* Bottle capacity is read from the rack's own title, e.g. "(3-9 Bottles)".
   * The larger number is its stated maximum. Racks that state no capacity are
   * skipped rather than guessed at. */
  var RANGE = /\((\d[\d,]*)\s*[–—-]\s*(\d[\d,]*)\s*bottles?\)/i;
  var SINGLE = /\((\d[\d,]*)\s*bottles?\)/i;

  function rackCapacity(title) {
    var m = RANGE.exec(title);
    if (m) return parseInt(m[2].replace(/,/g, ''), 10);
    m = SINGLE.exec(title);
    if (m) return parseInt(m[1].replace(/,/g, ''), 10);
    return null;
  }

  function pickRacks(racks, bottles) {
    if (!bottles || bottles < 1) return [];
    var out = [];
    for (var i = 0; i < racks.length; i++) {
      var r = racks[i];
      var cap = rackCapacity(r.title);
      if (!cap || !r.variants || !r.variants.length) continue;
      var qty = Math.ceil(bottles / cap);
      if (qty > 12) continue;
      var v = r.variants.filter(function (x) { return x.available; })[0] || r.variants[0];
      out.push({ rack: r, qty: qty, variant: v, capacity: cap, holds: cap * qty, total: v.price * qty });
    }
    out.sort(function (a, b) { return a.total - b.total || a.qty - b.qty; });
    return out.slice(0, 3);
  }

  function cartPath(lines) {
    var parts = [];
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].qty > 0) parts.push(lines[i].variantId + ':' + lines[i].qty);
    }
    return parts.length ? '/cart/' + parts.join(',') : null;
  }

  function quote(input, catalog) {
    var s = sizing(input);
    var picks = pickUnits(catalog.units || [], s.sizingVolume);
    var racks = pickRacks(catalog.racks || [], input.bottles);
    var flags = [];
    if (picks.length && picks[0].qty > 1) {
      flags.push('No single unit covers this room, so the quantities above use more than one. Placement and airflow start to matter once you run two.');
    }
    if (!picks.length) {
      flags.push('Three of our largest units still will not cover this room. Call us and we will size it by hand.');
    }
    if (input.standardDoor) {
      flags.push('An uninsulated door leaks around the clock. Swap it for a cellar door before the unit goes in and you will buy less cooling and pay less to run it.');
    }
    if (!input.vapourBarrier) {
      flags.push('Every capacity on this page assumes insulation and a sealed vapor barrier. Without one, moisture works on the walls whichever unit you buy.');
    }
    return {
      volume: s.volume, sizingVolume: s.sizingVolume, allowances: s.notes,
      btu: btuEstimate(input), picks: picks, racks: racks, flags: flags
    };
  }

  return {
    sizing: sizing, btuEstimate: btuEstimate, pickUnits: pickUnits,
    rackCapacity: rackCapacity, pickRacks: pickRacks, cartPath: cartPath,
    quote: quote, ALLOWANCE: ALLOWANCE
  };
});
